/*
 * API client for the FastAPI backend (backend/app.py).
 *
 * In dev (Vite on :5173) calls go through the Vite proxy to :8090. In prod,
 * the React app is served from the same origin as the API, so relative paths
 * just work. The client never hard-codes a host; this is intentional.
 */

const TRACE_KINDS = new Set([
  'plan',
  'tool_call',
  'verify',
  'done',
  'error',
  'usage',
  'stream_end',
]);

async function jsonOrThrow(response, label) {
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`${label} failed: ${response.status} ${body}`);
  }
  return response.json();
}

export async function getCorpus() {
  const response = await fetch('/corpus');
  return jsonOrThrow(response, 'GET /corpus');
}

export async function createQuery(query) {
  const response = await fetch('/queries', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  });
  return jsonOrThrow(response, 'POST /queries');
}

export async function getResults(queryId) {
  const response = await fetch(`/queries/${queryId}/results`);
  return jsonOrThrow(response, `GET /queries/${queryId}/results`);
}

export async function listQueries() {
  const response = await fetch('/queries');
  return jsonOrThrow(response, 'GET /queries');
}

export async function getTrace(queryId) {
  const response = await fetch(`/queries/${queryId}/trace`);
  return jsonOrThrow(response, `GET /queries/${queryId}/trace`);
}

export async function getUsage(queryId) {
  const response = await fetch(`/queries/${queryId}/usage`);
  return jsonOrThrow(response, `GET /queries/${queryId}/usage`);
}

export async function saveComment(queryId, resultId, text) {
  const response = await fetch(
    `/queries/${queryId}/results/${resultId}/comments`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    },
  );
  return jsonOrThrow(response, 'POST comment');
}

/**
 * Open an SSE stream and dispatch each TraceEvent to onEvent.
 *
 * Resolves with the final stream_end status (complete | empty | error) once
 * the server sends `kind=stream_end`. Network failure rejects.
 *
 * Returns an object with a .cancel() that the caller MUST invoke on unmount
 * to avoid leaking the EventSource.
 */
export function streamEvents(queryId, { onEvent, onEnd, onError }) {
  const source = new EventSource(`/queries/${queryId}/events`);
  let endStatus = null;
  let settled = false;

  const finish = (status) => {
    if (settled) return;
    settled = true;
    source.close();
    if (onEnd) onEnd(status);
  };

  source.onmessage = (event) => {
    let parsed;
    try {
      parsed = JSON.parse(event.data);
    } catch (err) {
      // Server occasionally emits keepalive comments; ignore non-JSON.
      return;
    }
    if (parsed && TRACE_KINDS.has(parsed.kind)) {
      onEvent(parsed);
    }
    if (parsed && parsed.kind === 'stream_end') {
      endStatus = parsed.status || 'complete';
      finish(endStatus);
    }
  };

  source.onerror = () => {
    // EventSource fires onerror on intentional close too; only treat as error
    // if we haven't already finished with a stream_end.
    if (settled) return;
    if (onError) onError(new Error('SSE connection failed'));
    finish(endStatus || 'error');
  };

  return {
    cancel: () => finish(endStatus || 'cancelled'),
  };
}
